import { CheckIcon, XIcon } from "lucide-react";
import { useState } from "react";

import type { ChatToolCall, ToolCallOutcome } from "~/ai-chat/model/chat-message";
import { formatToolCallDuration, toolCallSource } from "~/ai-chat/model/tool-activity";
import { ChatCopyButton } from "~/ai-chat/ui/chat-copy-button";
import { cn } from "~/utils/cn";

export function ChatToolTranscript({ calls }: { calls: ChatToolCall[] }) {
  return (
    <ol
      aria-label="Tool calls"
      className="border-border ml-1.5 flex max-w-full min-w-0 flex-col gap-3 border-l py-1 pl-3"
    >
      {calls.map((call, index) => {
        const source = toolCallSource(call);
        return (
          // Calls are append-only and never reordered, so the index is a stable key.
          <li key={index} className="flex min-w-0 flex-col gap-1">
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span className="text-foreground font-medium">
                {index + 1}. {call.tool}
              </span>
              {call.outcome ? <OutcomeBadge outcome={call.outcome} /> : null}
              {source ? (
                <>
                  <span>{source.language}</span>
                  <ChatCopyButton text={source.text} copiedMessage="Code copied." ariaLabel="Copy code" />
                </>
              ) : null}
            </div>
            {source ? (
              <pre className="bg-muted text-foreground overflow-x-auto rounded-md p-2 font-mono text-xs">
                <code>{source.text}</code>
              </pre>
            ) : (
              <span className="text-muted-foreground text-xs">Arguments too large to show.</span>
            )}
            {call.outcome ? <OutcomeSummary outcome={call.outcome} /> : null}
          </li>
        );
      })}
    </ol>
  );
}

function OutcomeBadge({ outcome }: { outcome: ToolCallOutcome }) {
  const Icon = outcome.ok ? CheckIcon : XIcon;
  return (
    <span className={cn("flex items-center gap-1", outcome.ok ? "text-success" : "text-destructive")}>
      <Icon className="size-3" aria-hidden="true" />
      <span className="sr-only">{outcome.ok ? "Succeeded" : "Failed"}</span>
      {outcome.durationMs === null ? null : <span>{formatToolCallDuration(outcome.durationMs)}</span>}
    </span>
  );
}

// A failure leads with its error; the rest (the SDK's hint for the model) waits behind "Show details".
function OutcomeSummary({ outcome }: { outcome: ToolCallOutcome }) {
  const [expanded, setExpanded] = useState(false);
  const [firstLine, ...rest] = (outcome.summary || "(no output)").split("\n");
  const details = rest.join("\n").trim();
  return (
    <div className={cn("flex flex-col gap-1 text-xs", outcome.ok ? "text-muted-foreground" : "text-destructive")}>
      <p className="font-mono break-words whitespace-pre-wrap">
        → {firstLine}
        {expanded && details ? `\n${details}` : null}
      </p>
      {details ? (
        <button
          type="button"
          onClick={() => setExpanded((shown) => !shown)}
          aria-expanded={expanded}
          className="text-muted-foreground hover:text-foreground w-fit transition-colors"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      ) : null}
    </div>
  );
}
